import React from 'react';
import {
  LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

export default function EmployeeHoursTrendChart({ dataset }) {
  const chartData = React.useMemo(() => {
    if (!dataset) return [];
    return dataset?.labels?.map((label, index) => {
      const item = { period: label };
      if (dataset?.totalPaidHours?.length) {
        item['Actual Hours'] = dataset?.totalPaidHours[index] || 0;
      }
      if (dataset?.calculatedDailyHours?.length) {
        item['Allowed Hours'] = dataset?.calculatedDailyHours[index] || 0;
      }
      return item;
    });
  }, [dataset]);

  const hasData = chartData?.some(item =>
    (item['Actual Hours'] && item['Actual Hours'] > 0) ||
    (item['Allowed Hours'] && item['Allowed Hours'] > 0)
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      const actual = payload.find(p => p.dataKey === 'Actual Hours')?.value || 0;
      const allowed = payload.find(p => p.dataKey === 'Allowed Hours')?.value || 0;
      const utilization = allowed > 0 ? ((actual / allowed) * 100).toFixed(1) : 0;

      return (
        <div className="bg-white p-4 border border-gray-300 rounded shadow-lg">
          <p className="font-medium text-gray-800">{label}</p>
          {payload?.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {`${entry.name}: ${entry.value.toFixed(2)} hrs`}
            </p>
          ))}
          {actual > 0 && allowed > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <p className="text-sm text-gray-600">Utilization: {utilization}%</p>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white p-6">
      <div className="mb-4">
        <p className="text-2xl text-gray-800">Employee Hours Trend Analysis</p>
      </div>

      {hasData ? (
        <div style={{ width: '100%', height: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="period"
                angle={-15} 
                textAnchor="end" 
                interval="preserveStartEnd" 
                tick={{ fontSize: 12 }} 
                label={{ value: "", offset: 30, fontSize: 12, fill: "#555" }} 
              />
              <YAxis label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend verticalAlign="top" height={36} />

              {dataset?.totalPaidHours?.length > 0 && (
                <Line
                  type="monotone"
                  dataKey="Actual Hours"
                  stroke="#9369A9"
                  strokeWidth={3}
                  dot={{ fill: '#9369A9', r: 5 }}
                  activeDot={{ r: 7, fill: '#9369A9' }}
                />
              )}

              {dataset?.calculatedDailyHours?.length > 0 && (
                <Line
                  type="monotone"
                  dataKey="Allowed Hours"
                  stroke="#ff7043"
                  strokeWidth={3}
                  strokeDasharray="5 5"
                  dot={{ fill: '#ff7043', r: 5 }}
                  activeDot={{ r: 7, fill: '#ff7043' }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex items-center justify-center h-96 text-gray-400 text-lg">
          No employee hours data available.
        </div>
      )}
    </div>
  );
}
